import { HTMLProps, ReactNode, useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/utils/helpers";
import Button from "@/components/common/Button";
import Overlay from "@/components/common/Overlay";
import { SvgIconX } from "@/components/common/Svg";
import PanelBackdrop, { PanelBackdropProps } from "./components/PanelBackdrop";
import styles from "./Panel.module.css";

const PANEL_DURATION = 1;

export interface PanelProps extends HTMLProps<HTMLElement> {
	open: boolean;
	defaultOpen?: true;
	modal?: boolean;
	position?: "left" | "right" | "center";
	title?: string;
	description?: string;
	tools?: string | ReactNode;
	hideHeading?: boolean;
	headingProps?: HTMLProps<HTMLDivElement>;
	bodyProps?: HTMLProps<HTMLDivElement>;
	closeProps?: Dialog.DialogCloseProps;
	backdropProps?: PanelBackdropProps;
	onClose?: () => void;
	onCloseComplete?: () => void;
	onBackdropClick?: () => void;
}

export default function Panel({
	open,
	defaultOpen,
	modal = false,
	position = "center",
	title,
	description,
	tools,
	hideHeading,
	headingProps,
	bodyProps,
	closeProps,
	backdropProps,
	onClose,
	onCloseComplete,
	onBackdropClick,
	className,
	children
} : PanelProps) {
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
	const [backdropRendered, setBackdropRendered] = useState<boolean>(false);

	const handleOpenChange = (newOpen: boolean) => {
		// When closing panel
		if(newOpen === false) {
			// Immediately call `onClose` callback
			if(onClose) onClose();
			// Delay call `onCloseComplete` callback until after animation
			if(onCloseComplete) setTimeout(onCloseComplete, PANEL_DURATION);
		}
	}

	// Disable outside interaction handler
	const handleOutside = (e: Event) => e.preventDefault();

	useEffect(() => {
		if(open) setBackdropRendered(open);
		setBackdropOpen(open);
		// Delay the `open` value on `Dialog.Root` to await animation before removing from DOM
		// const openDialogTimeout = setTimeout(() => {
			setDialogOpen(open);
			setBackdropRendered(open);
		// }, open ? 0 : PANEL_DURATION);
		// Clear timeout when destroyed so delays don't overlap
		// return () => clearTimeout(openDialogTimeout);
	}, [open]);

	useEffect(() => {
		document.body.style.overflowY = dialogOpen ? "hidden" : "auto";
		// Also prevent scrolling
		// const handleWindowScroll = (event: Event) => {
		// 	event.preventDefault();
		// 	return false;
		// }
		// if(dialogOpen) {
		// 	window.addEventListener("wheel", handleWindowScroll, { passive: false });
		// 	window.addEventListener("touchmove", handleWindowScroll, { passive: false });
		// } else {
		// 	window.removeEventListener("wheel", handleWindowScroll);
		// 	window.removeEventListener("touchmove", handleWindowScroll);
		// }
		// return () => {
		// 	window.removeEventListener("wheel", handleWindowScroll);
		// 	window.removeEventListener("touchmove", handleWindowScroll);
		// }
	}, [dialogOpen]);

	return (
		<Dialog.Root
			open={dialogOpen}
			defaultOpen={defaultOpen}
			modal={modal}
			onOpenChange={handleOpenChange}
		>
			{backdropRendered ?
				<PanelBackdrop
					{...backdropProps}
					show={open && backdropOpen && backdropProps?.show}
					onClick={onBackdropClick}
				/>
			: null}
			<Dialog.Content
				// asChild={true}
				forceMount={defaultOpen}
				onInteractOutside={handleOutside}
				onPointerDownOutside={handleOutside}
				// aria-hidden={!open}
				className={cn(
					styles.Panel,
					position ? styles[`Panel_${position}`] : null,
					hideHeading ? styles.Panel_hideHeading : null,
					className
				)}
			>
				<Overlay
					className={styles.PanelOverlay}
				>
					{onClose ?
						<Dialog.Close
							asChild={true}
							{...closeProps}
						>
							<Button
								square
								title="Close"
								onClick={onClose}
								className={styles.PanelClose}
							>
								<SvgIconX />
							</Button>
						</Dialog.Close>
					: null}
					<div
						{...headingProps}
						className={cn(
							styles.PanelHeading,
							headingProps?.className
						)}
					>
						<Dialog.Title
							className={styles.PanelTitle}
						>
							{title}
						</Dialog.Title>
					</div>
					<div
						{...bodyProps}
						className={cn(
							styles.PanelBody,
							bodyProps?.className
						)}
					>
						<div
							className={styles.PanelSubheading}
						>
							<div
								className={styles.PanelSubheadingDesc}
							>
								<Dialog.Description>
									{description}
								</Dialog.Description>
							</div>
							<div
								className={styles.PanelSubheadingTools}
							>
								{tools}
							</div>
						</div>
						{children}
					</div>
				</Overlay>
			</Dialog.Content>
		</Dialog.Root>
	)
}